/**
 * What the picked dataset holds, before anything runs on it: how many items fill
 * each slot (input, expected output, a recorded output, metadata) and its first
 * few items. The slots say what the next steps can do: a recorded output means
 * the agent need not run, an expected output is what reference evaluators need.
 */

import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router";
import { useDatasetItems } from "@/features/datasets/hooks";
import type { Dataset, DatasetItem } from "@/features/datasets/types";
import { formatCount } from "@/lib/format";
import { isBlank, oneLine } from "@/lib/json";

const SHOWN = 3;

type Slot = "input" | "expected_output" | "output" | "metadata";

const SLOTS: Array<{ key: Slot; label: string; note: string }> = [
  { key: "input", label: "Input", note: "What the agent is sent" },
  { key: "expected_output", label: "Expected output", note: "For evaluators that compare to a reference" },
  { key: "output", label: "Recorded output", note: "Score these without running the agent" },
  { key: "metadata", label: "Metadata", note: "Never sent to the agent" },
];

export function DatasetPreview({ dataset }: { dataset: Dataset }) {
  const version = dataset.latest_version?.version ?? null;
  const page = useDatasetItems(dataset.id, version, 1).data;
  if (!page) return <div aria-hidden className="h-40 animate-pulse rounded-[9px] bg-subtle" />;
  const { items, total } = page;
  const partial = total > items.length;

  return (
    <section
      aria-label="Dataset preview"
      className="flex flex-col gap-3 rounded-[9px] border border-line-soft bg-well p-4"
    >
      <div className="flex items-baseline gap-2">
        <h3 className="text-[13px] font-semibold">What's inside</h3>
        <span className="text-xs text-muted">
          {formatCount(total)} items{partial ? ` · counts from the first ${formatCount(items.length)}` : ""}
        </span>
        <Link to={`/datasets/${dataset.id}`} className="link ml-auto flex items-center gap-0.5 text-xs">
          Open dataset <ArrowUpRight aria-hidden className="size-3" />
        </Link>
      </div>
      <ul className="grid grid-cols-2 gap-2">
        {SLOTS.map(({ key, label, note }) => (
          <SlotTile
            key={key}
            label={label}
            note={note}
            filled={items.filter((i) => !isBlank(i[key])).length}
            of={items.length}
          />
        ))}
      </ul>
      {items.length > 0 && <FirstItems items={items.slice(0, SHOWN)} />}
    </section>
  );
}

function SlotTile(props: { label: string; note: string; filled: number; of: number }) {
  const { filled, of } = props;
  const share = of ? filled / of : 0;
  return (
    <li
      className={`flex flex-col gap-1.5 rounded-lg border bg-surface p-2.5 ${filled ? "border-line" : "border-line-soft"}`}
    >
      <span className="flex items-baseline justify-between gap-2">
        <span className={`text-xs font-medium ${filled ? "text-ink" : "text-dim"}`}>{props.label}</span>
        <span className="text-xs text-muted tabular-nums">
          {filled === of ? (filled ? "all" : "none") : `${filled}/${of}`}
        </span>
      </span>
      <span aria-hidden className="h-1 overflow-hidden rounded-full bg-track">
        <span className="block h-full rounded-full bg-link" style={{ width: `${share * 100}%` }} />
      </span>
      <span className="text-[11px] leading-snug text-faint">{props.note}</span>
    </li>
  );
}

function FirstItems({ items }: { items: DatasetItem[] }) {
  return (
    <table className="tbl w-full table-fixed text-xs">
      <thead>
        <tr>
          <th className="w-28">Item</th>
          <th>Input</th>
          <th>Expected output</th>
        </tr>
      </thead>
      <tbody>
        {items.map((i) => (
          <tr key={i.id}>
            <td className="truncate font-mono text-[11px] text-muted">{i.key}</td>
            <td className="truncate font-mono text-[11px]">{oneLine(i.input, 120)}</td>
            <td className="truncate font-mono text-[11px] text-muted">
              {isBlank(i.expected_output) ? "—" : oneLine(i.expected_output, 120)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
